import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  FlyToBoundsRefProps,
  IAnimationState,
} from "../components/Leaflet/FlyToBounds/Index";
import { useFilter } from "./useFilter";
import axios from "../services/axios";
import Constants from "../constants/Index";
import { getCenterCoords } from "../utils/leaflet/getCenterCoords";
import { ICoords, ICoord } from "../@types/GoogleMaps/ICoord";
import { IFarms } from "../@types/API/IFarm";
import { IBatch } from "../@types/API/IBatches";
import { useData } from "./useData";

type ICustomFarm = {
  id: string;
  initials: string;
  coords: ICoords;
  name: string;
  bgColor: string;
};

type ICustomArea = {
  id: string;
  initials: string;
  coords: ICoords;
  name: string;
  bgColor: string;
  farmId?: string;
  batches: Array<{
    visualization: boolean;
    coord: ICoords | undefined;
    microAreaId: string;
  }>;
};

type ICustomMicroArea = {
  areaId: string;
  id: string;
  initials: string;
  coords: ICoords;
  currentMicroArea?: boolean;
  farmId?: string;
  areaInitials?: string;
  batchVisualization: boolean;
};

export function usePaddockMapData() {
  const flytoBoundsRef = useRef<FlyToBoundsRefProps>(null);
  const [stateOfAnimation, setStateOfAnimation] =
    useState<IAnimationState>("end");
  const [farms, setFarms] = useState<IFarms>([]);
  const [areas, setAreas] = useState<IArea[]>([]);
  const [errorModal, setErrorModal] = useState<boolean>(false);
  const [batchByMicroAreaMap] = useState(new Map<number, IBatch>());

  const {customBatches: batchesIcon, setCustomBatches, setBatches, microAreas, setMicroAreas } = useData();

  const {
    farmFilter,
    areaFilter,
    microAreaFilter,
    batchFilter,
    setHideMicroAreaFilter,
    setHideBatchFilter,
  } = useFilter();

  // Função para verificar visualização de batch
  const handleBatchVisualization = useCallback(
    (farmId: string, areaId: string, microAreaId?: string) => {
      if (batchFilter && farmId === farmFilter.id && areaFilter)
        return (
          areaFilter.id === areaId && microAreaId === batchFilter.microAreaId
        );
      if (areaFilter && farmId === farmFilter.id)
        return areaFilter.id === areaId;
      return true;
    },
    [batchFilter, areaFilter, farmFilter],
  );

  // Carregar farms
  const loadFarms = useCallback(async () => {
    try {
      const { data } = await axios.get("/farms/user");
      setFarms(data);
      return data;
    } catch (error) {
      console.error("Erro ao carregar farms:", error);
      setErrorModal(true);
      return [];
    }
  }, []);

  // Carregar areas
  const loadAreas = useCallback(async () => {
    try {
      const { data } = await axios.get("/areas/user");
      setAreas(data);
      return data;
    } catch (error) {
      console.error("Erro ao carregar areas:", error);
      setErrorModal(true);
      return [];
    }
  }, []);

  // Carregar microareas
  const loadMicroAreas = useCallback(async (farmId: string) => {
    try {
      const { data } = await axios.get("/micro-areas", {
        params: { farm_id: farmId },
      });
      setMicroAreas(data);
      return data;
    } catch (error) {
      console.error("Erro ao carregar microareas:", error);
      setErrorModal(true);
      return [];
    }
  }, []);

  // Carregar batches
  const loadBatches = useCallback(
    async (farmId: string) => {
      try {
        const { data: batches } = await axios.get<IBatch[]>(`/batches`, {
          params: { farm_id: farmId },
        });

        const batchIcons = batches.map((batch) => {
          const microAreaId = Number(batch.current_micro_area.id);
          batchByMicroAreaMap.set(microAreaId, batch);

          return {
            initials: batch.initials,
            coords: getCenterCoords(batch.current_micro_area.map_coords),
            id: batch.id.toString(),
            farmId: farmId,
            areaId: batch.area.id.toString(),
            visible: true,
            currentMicroArea: microAreaId,
          };
        });

        setCustomBatches(batchIcons);
        setBatches(batches);
      } catch (error) {
        console.error("Erro ao carregar batches:", error);
        setErrorModal(true);
      }
    },
    [batchByMicroAreaMap],
  );

  // Inicializar dados
  const initializeData = useCallback(async () => {
    const farmsData = await loadFarms();
    if (farmsData.length > 0) {
      await loadAreas();
    }
  }, [loadFarms, loadAreas]);

  // Centralizar mapa
  const centerMap = useCallback(
    (coords: ICoords) => {
      if (flytoBoundsRef.current?.canZoomAnimate(coords, 50)) {
        flytoBoundsRef.current.flyToBounds(coords, {
          duration: areaFilter ? 0.5 : 5,
        });
      } else {
        setStateOfAnimation("end");
      }
    },
    [areaFilter],
  );

  // Dados memoizados para evitar re-renderização
  const memoizedData = useMemo(
    () => ({
      farms,
      areas,
      microAreas,
      batchesIcon,
      batchByMicroAreaMap,
      errorModal,
      stateOfAnimation,
    }),
    [
      farms,
      areas,
      microAreas,
      batchesIcon,
      batchByMicroAreaMap,
      errorModal,
      stateOfAnimation,
    ],
  );

  // Efeitos para reagir a mudanças de filtros
  useEffect(() => {
    if (farmFilter) {
      centerMap(farmFilter.coords);
      loadMicroAreas(farmFilter.id);
      loadBatches(farmFilter.id);
    }
  }, [farmFilter, loadMicroAreas, loadBatches]);

  useEffect(() => {
    if (areaFilter) {
      centerMap(areaFilter.coords);
    }
  }, [areaFilter, centerMap]);

  // Inicializar dados na montagem
  useEffect(() => {
    initializeData();
    setHideMicroAreaFilter(false);
    setHideBatchFilter(false);
  }, [initializeData, setHideMicroAreaFilter, setHideBatchFilter]);

  const toggleErrorModal = useCallback(() => {
    setErrorModal((prev) => !prev);
  }, []);

  return {
    ...memoizedData,
    flytoBoundsRef,
    setStateOfAnimation,
    centerMap,
    toggleErrorModal,
    handleBatchVisualization,
  };
}
